<script setup lang="ts">
import { type Component, computed } from 'vue'
import { useTranslate } from '@tolgee/vue'

import type { IconStyleType, TopStat, ImpactColor } from '@/api/interfaces'
import { formatNumberByLocale, formatTotalAmounts, snakeCase } from '@/helpers/utils'

import CarbonImpactIcon from '@/components/icons/impact/CarbonImpactIcon.vue'
import MonthsEarthPositiveImpactIcon from '@/components/icons/impact/MonthsEarthPositiveImpactIcon.vue'
import PlasticImpactIcon from '@/components/icons/impact/PlasticImpactIcon.vue'
import TreesImpactIcon from '@/components/icons/impact/TreesImpactIcon.vue'
import KelpImpactIcon from '@/components/icons/impact/KelpImpactIcon.vue'
import WaterImpactIcon from '@/components/icons/impact/WaterImpactIcon.vue'
import BeesImpactIcon from '@/components/icons/impact/BeesImpactIcon.vue'
import BaseTooltip from '@/components/common/BaseTooltip.vue'

interface Props {
  type: TopStat
  amount: number
  colorSettings: ImpactColor
  iconStyle: IconStyleType
  relationImpact?: number
  transferredImpact?: number
}

const props = defineProps<Props>()
const { t } = useTranslate()

const icons: Record<TopStat, Component> = {
  carbon: CarbonImpactIcon,
  monthsEarthPositive: MonthsEarthPositiveImpactIcon,
  plastic: PlasticImpactIcon,
  trees: TreesImpactIcon,
  kelp: KelpImpactIcon,
  water: WaterImpactIcon,
  bees: BeesImpactIcon,
}

const isOffset = computed(() => props.type !== 'monthsEarthPositive')
const relationImpactText = computed(
  () =>
    `${formatNumberByLocale(props.relationImpact || 0)} ${t.value(
      `PublicTopStatCard.tooltip.partnerImpact.${props.type}`,
      {
        value: props.relationImpact || 0,
      },
    )}`,
)
const transferredImpactText = computed(
  () =>
    `${formatNumberByLocale(props.transferredImpact || 0)} ${t.value(
      `PublicTopStatCard.tooltip.transferredImpact.${props.type}`,
      {
        value: props.transferredImpact || 0,
      },
    )}`,
)
const tooltipContent = computed(() => {
  if (!!props.relationImpact && !!props.transferredImpact && props.transferredImpact > 0) {
    return `${relationImpactText.value} <br /> ${transferredImpactText.value}`
  }

  if (!!props.relationImpact && props.relationImpact > 0) {
    return relationImpactText.value
  }

  return transferredImpactText.value
})
</script>

<template>
  <div class="public-top-stat-card" :style="{ backgroundColor: colorSettings.backgroundColor }">
    <div class="public-top-stat-card-content" :style="{ color: colorSettings.textColor }">
      <strong class="public-top-stat-card-text amount">
        {{
          isOffset
            ? formatTotalAmounts(amount)
            : t('PublicTopStatCard.months_amount', { value: amount, count: amount })
        }}
      </strong>
      <p class="public-top-stat-card-text">
        {{ t(`PublicTopStatCard.${snakeCase(type)}`, { value: amount }) }}
        <BaseTooltip
          v-if="(!!relationImpact || !!transferredImpact) && $route.name === 'PublicPage'"
          :content="tooltipContent"
          :color="colorSettings.textColor"
        />
      </p>
    </div>
    <component class="public-top-stat-card-icon" :is="icons[props.type]" />
  </div>
</template>

<style scoped lang="scss">
.public-top-stat-card {
  border-radius: 10px;
  padding: 20px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--gray-light-db);
  width: 100%;
  min-height: 130px;

  @include media('sm-and-up') {
    width: 49%;
  }

  @include media('lg-and-up') {
    max-width: 274px;
  }

  :deep(.tooltip) {
    margin-top: 3px;
  }

  .community-page & {
    background-color: rgba(249, 249, 249, 0.2) !important;
    border: 1px solid #fafafa;
    border-radius: 10px;
    backdrop-filter: blur(10px);
  }
}

.public-top-stat-card-icon {
  flex: 0 0 auto;
  max-width: 65px;
}

.public-top-stat-card-text {
  font-size: 20px;

  &.amount {
    font-size: 38px;
    font-weight: 700;
    margin-bottom: 4px;
  }
}
</style>
